import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { MIN_CHARS_TO_SEARCH } from '@/constants/search';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import useBetaFeature from '@/hooks/useBetaFeature';
import PersonRepository from '@/repositories/PersonRepository';
import { PersonMinimal } from '@/types/person';
import Spinner from '@/ui/components/Spinner';
import { getPersonNavOptions } from '@/utils/personNavOptions';
import { getAvailableWorkspaceNavOptions } from '@/utils/workspaceNavOptions';

type ActiveTab = 'person' | 'workspace' | 'people';

const TAB_LABELS: Record<ActiveTab, string> = {
  people: 'People',
  person: 'Person',
  workspace: 'Workspace'
};

interface GlobalSearchProps {
  onClose: () => void;
}

const GlobalSearch = ({ onClose }: GlobalSearchProps) => {
  const { currentUser, permissions } = useContext(AppContext);
  const { isEnabled: isInsurancePageHidden } = useBetaFeature('profileInsurance');
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<PersonMinimal | null>(null);
  const [hasAutoSelectedCurrentPerson, setHasAutoSelectedCurrentPerson] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const personRepository = PersonRepository();

  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Detect if we're on a person page
  const currentPersonIdFromUrl = useMemo(() => {
    const match = location.pathname.match(/\/people\/([a-f0-9-]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Default to Search when no person is selected.
  const [activeTab, setActiveTab] = useState<ActiveTab>('people');

  // Workspace navigation options
  const workspaceNavOptions = useMemo(
    () => getAvailableWorkspaceNavOptions(permissions),
    [permissions]
  );

  // Filtered workspace options
  const filteredWorkspaceOptions = useMemo(
    () =>
      workspaceNavOptions
        .filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((option) => [option.label, option.path] as const),
    [workspaceNavOptions, searchTerm]
  );

  // Person section options
  const personNavOptions = useMemo(() => {
    if (!selectedPerson) return [];
    return getPersonNavOptions(selectedPerson.id, permissions!, { isInsurancePageHidden });
  }, [selectedPerson, permissions, isInsurancePageHidden]);

  const filteredSectionOptions = useMemo(() => {
    if (!selectedPerson) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return personNavOptions;
    return personNavOptions.filter(({ label }) => label.toLowerCase().includes(term));
  }, [personNavOptions, searchTerm, selectedPerson]);

  const visibleTabs = useMemo(
    () =>
      selectedPerson
        ? (['people', 'person', 'workspace'] as ActiveTab[])
        : (['people', 'workspace'] as ActiveTab[]),
    [selectedPerson]
  );

  // People search
  const trimmedSearchTerm = debouncedSearchTerm.trim();
  const { data, isPending } = useAPI({
    queryKey: ['globalSearchPeople', currentUser?.id, trimmedSearchTerm],
    queryFn: async () => personRepository.getPeople({ search: trimmedSearchTerm, page: '1' }),
    enabled:
      !!currentUser?.id && trimmedSearchTerm.length >= MIN_CHARS_TO_SEARCH && activeTab === 'people'
  });

  // Active list for keyboard navigation
  const activeList = useMemo(() => {
    if (activeTab === 'workspace') {
      return filteredWorkspaceOptions.map(([name, path]) => ({ label: name, path }));
    }
    if (activeTab === 'people') {
      return (data?.data?.results ?? []).map((p: PersonMinimal) => ({
        label: `${p.first_name} ${p.last_name}`,
        path: `/people/${p.id}/profile`
      }));
    }
    // Person tab
    if (selectedPerson) {
      return filteredSectionOptions.map(({ label, path }) => ({ label, path }));
    }
    return [];
  }, [
    activeTab,
    filteredWorkspaceOptions,
    selectedPerson,
    filteredSectionOptions,
    data?.data?.results
  ]);

  // Reset highlight when list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [activeList, activeTab]);

  useEffect(() => {
    if (!selectedPerson && activeTab === 'person') {
      setActiveTab('people');
    }
  }, [selectedPerson, activeTab]);

  // Scroll highlighted item into view
  useEffect(() => {
    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Allow one auto-select per person-id context.
  useEffect(() => {
    setHasAutoSelectedCurrentPerson(false);
  }, [currentPersonIdFromUrl]);

  // Fetch current person from URL
  const { data: currentPersonData } = useAPI({
    queryKey: ['globalSearchCurrentPerson', currentPersonIdFromUrl, currentUser?.id],
    queryFn: async () => {
      if (!currentPersonIdFromUrl) return null;
      try {
        const response = await personRepository.getPersonProfile({
          personId: currentPersonIdFromUrl
        });
        if (!response.isError && response.data) {
          return {
            id: response.data.id,
            first_name: response.data.first_name,
            last_name: response.data.last_name,
            nickname: response.data.nickname,
            profile_image_url: response.data.profile_image_url,
            pronouns: response.data.pronouns,
            time_zone: response.data.time_zone
          } as PersonMinimal;
        }
        return null;
      } catch {
        return null;
      }
    },
    enabled: !!currentPersonIdFromUrl && !selectedPerson
  });

  // Auto-select current person when palette opens on a person page
  useEffect(() => {
    if (
      currentPersonData &&
      !selectedPerson &&
      currentPersonIdFromUrl &&
      !hasAutoSelectedCurrentPerson
    ) {
      setSelectedPerson(currentPersonData);
      setSearchTerm('');
      setActiveTab('person');
      setHasAutoSelectedCurrentPerson(true);
    }
  }, [currentPersonData, currentPersonIdFromUrl, selectedPerson, hasAutoSelectedCurrentPerson]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on Esc / click outside
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleClickOutside = (e: Event) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (activeList.length > 0) {
        setHighlightedIndex((prev) => Math.min(prev + 1, activeList.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (activeList.length > 0) {
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Handle People tab: select person and switch to Person tab
      if (activeTab === 'people') {
        const person = (data?.data?.results ?? [])[highlightedIndex] as PersonMinimal | undefined;
        if (person) {
          setSelectedPerson(person);
          setActiveTab('person');
          setSearchTerm('');
          return;
        }
      }
      const item = activeList[highlightedIndex] as { label: string; path: string };
      if (!item) {
        if (activeTab === 'person' && selectedPerson && searchTerm.trim() === '') {
          navigate(`/people/${selectedPerson.id}/profile`);
          onClose();
        }
        return;
      }
      if (item.path) {
        navigate(item.path);
        onClose();
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      setActiveTab((prev) => {
        const currentIndex = visibleTabs.indexOf(prev);
        if (currentIndex === -1) {
          return visibleTabs[0];
        }
        const nextIndex = e.shiftKey
          ? (currentIndex - 1 + visibleTabs.length) % visibleTabs.length
          : (currentIndex + 1) % visibleTabs.length;
        return visibleTabs[nextIndex];
      });
      setSearchTerm('');
    }
  };

  const personPlaceholder = selectedPerson
    ? `Filter ${selectedPerson.first_name}'s sections...`
    : 'Select a person from People tab...';

  const workspacePlaceholder = 'Filter workspaces…';
  const peoplePlaceholder = 'Search people...';

  return (
    <div className="fixed inset-0 backdrop-blur z-50 flex items-start justify-center pt-20">
      <div ref={overlayRef} className="w-full max-w-2xl mx-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Search input */}
          <div className="px-4 pt-4">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                activeTab === 'person'
                  ? personPlaceholder
                  : activeTab === 'workspace'
                    ? workspacePlaceholder
                    : peoplePlaceholder
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-0 mt-3 px-4 border-b border-gray-200">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => {
                  setActiveTab(tab);
                  setSearchTerm('');
                  inputRef.current?.focus();
                }}
                className={`px-4 py-2 text-sm font-medium capitalize -mb-px border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {TAB_LABELS[tab]}
              </button>
            ))}
            <div className="ml-auto self-center pr-1 text-xs text-gray-400 select-none">
              Tab to switch
            </div>
          </div>

          {/* Results */}
          <div className="max-h-72 overflow-y-auto">
            {/* ── Workspace tab ── */}
            {activeTab === 'workspace' && (
              <>
                {filteredWorkspaceOptions.length > 0 ? (
                  filteredWorkspaceOptions.map(([name, path], index) => (
                    <button
                      key={path}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      type="button"
                      onClick={() => {
                        navigate(path);
                        onClose();
                      }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 border-b border-gray-100 last:border-b-0 ${
                        index === highlightedIndex ? 'bg-blue-50 text-blue-700' : ''
                      }`}>
                      <span className="font-medium">{name}</span>
                      <span className="ml-2 text-xs text-gray-400">{path}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center text-sm text-gray-400">
                    No workspaces match {searchTerm}
                  </div>
                )}
              </>
            )}

            {/* ── Person tab ── */}
            {activeTab === 'person' && (
              <>
                {/* Selected person header */}
                {selectedPerson && (
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <span className="mp-sensitive text-sm font-medium text-gray-700">
                      {selectedPerson.first_name} {selectedPerson.last_name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPerson(null);
                        setSearchTerm('');
                        setActiveTab('people');
                        setHighlightedIndex(0);
                        inputRef.current?.focus();
                      }}
                      className="text-xs text-gray-400 hover:text-gray-600">
                      Find different person
                    </button>
                  </div>
                )}

                {/* Person sections */}
                {selectedPerson &&
                  filteredSectionOptions.length > 0 &&
                  filteredSectionOptions.map(({ label, path }, index) => (
                    <button
                      key={label}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      type="button"
                      onClick={() => {
                        navigate(path);
                        onClose();
                      }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 border-b border-gray-100 last:border-b-0 ${
                        index === highlightedIndex ? 'bg-blue-50 text-blue-700' : ''
                      }`}>
                      {label}
                    </button>
                  ))}

                {selectedPerson && filteredSectionOptions.length === 0 && (
                  <div className="px-4 py-6 text-center text-sm text-gray-400">
                    No sections match {searchTerm}
                  </div>
                )}

                {!selectedPerson && (
                  <div className="px-4 py-6 text-center text-sm text-gray-400">
                    Pick a person from the People tab to see their sections here.
                  </div>
                )}
              </>
            )}

            {/* ── People tab ── */}
            {activeTab === 'people' && (
              <>
                {isPending && debouncedSearchTerm.trim().length >= MIN_CHARS_TO_SEARCH && (
                  <div className="py-6 flex justify-center">
                    <Spinner />
                  </div>
                )}
                {!isPending &&
                  debouncedSearchTerm.trim().length >= MIN_CHARS_TO_SEARCH &&
                  (data?.data?.results?.length ?? 0) > 0 &&
                  (data?.data?.results ?? []).map((person: PersonMinimal, index: number) => (
                    <button
                      key={person.id}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      type="button"
                      onClick={() => {
                        setSelectedPerson(person);
                        setActiveTab('person');
                        setSearchTerm('');
                      }}
                      className={`mp-sensitive w-full text-left px-4 py-2.5 hover:bg-gray-100 border-b border-gray-100 last:border-b-0 ${
                        index === highlightedIndex ? 'bg-blue-50 text-blue-700' : ''
                      }`}>
                      {person.first_name} {person.last_name}
                    </button>
                  ))}
                {!isPending &&
                  debouncedSearchTerm.trim().length >= MIN_CHARS_TO_SEARCH &&
                  (data?.data?.results?.length ?? 0) === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                      No people found for {debouncedSearchTerm}
                    </div>
                  )}
                {debouncedSearchTerm.trim().length < MIN_CHARS_TO_SEARCH && (
                  <div className="px-4 py-6 text-center text-sm text-gray-400">
                    Type at least {MIN_CHARS_TO_SEARCH} characters to search for a person
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400 flex gap-4">
            <span>↑↓ navigate</span>
            <span>Enter select</span>
            <span>Tab switch tab</span>
            <span>Esc close</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
